import {
  disableMFA as disableMFAMutation,
  forgetTrustedDevice as forgetTrustedDeviceMutation,
  setupMFA as setupMFAMutation,
  verifyMFA as verifyMFAMutation,
  verifyMFASetup as verifyMFASetupMutation,
  type SessionTokens,
  type TrustedDevice,
} from "queries/mfa";
import React, { createContext, useContext, useState, type FC } from "react";

/**
 * MFA Context State Interface
 */
export interface MFAContextState {
  // Setup state
  isSettingUp: boolean;
  qrCodeUrl: string | null;
  secretKey: string | null;
  setupComplete: boolean;

  // Verification state
  isVerifying: boolean;

  // Methods
  initializeSetup: (mfaSession?: string) => Promise<{
    qrCodeUrl: string;
    secretKey: string;
    setupComplete: boolean;
  }>;
  completeSetup: (
    code: string,
    mfaSession?: string
  ) => Promise<{
    success: boolean;
    sessionTokens?: {
      access: string;
      refresh: string;
      id: string;
    } | null;
  }>;
  verifyCode: (options: {
    username: string;
    code: string;
    session: string;
  }) => Promise<{
    success: boolean;
    sessionTokens?: SessionTokens;
    requiresSetup?: boolean;
  }>;
  revokeDevice: (deviceKey: string) => Promise<boolean>;
  disableMFA: () => Promise<boolean>;
}

export type { TrustedDevice };

const MFAContext = createContext<MFAContextState | undefined>(undefined);

interface MFAProviderProps {
  children: React.ReactNode;
}

export const MFAProvider: FC<MFAProviderProps> = ({ children }) => {
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [secretKey, setSecretKey] = useState<string | null>(null);
  const [setupComplete, setSetupComplete] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const initializeSetup = React.useCallback(async (mfaSession?: string) => {
    setIsSettingUp(true);
    try {
      const result = await setupMFAMutation({ mfaSession });
      if (result.data) {
        setQrCodeUrl(result.data.setupMFA.qrCodeUrl);
        setSecretKey(result.data.setupMFA.secretKey);
        setSetupComplete(result.data.setupMFA.setupComplete);
        return result.data.setupMFA;
      }
      throw new Error("Failed to initialize MFA setup");
    } finally {
      setIsSettingUp(false);
    }
  }, []);

  const completeSetup = React.useCallback(
    async (code: string, mfaSession?: string) => {
      try {
        const result = await verifyMFASetupMutation({ code, mfaSession });
        if (result.data) {
          const setupResult = result.data.verifyMFASetup;
          setSetupComplete(Boolean(setupResult.success));
          return {
            success: Boolean(setupResult.success),
            sessionTokens: (setupResult.sessionTokens ?? null) as {
              access: string;
              refresh: string;
              id: string;
            } | null,
          };
        }
        return { success: false, sessionTokens: null };
      } catch {
        return { success: false, sessionTokens: null };
      }
    },
    []
  );

  const verifyCode = React.useCallback(
    async (options: { username: string; code: string; session: string }) => {
      setIsVerifying(true);
      try {
        const result = await verifyMFAMutation({
          username: options.username,
          code: options.code,
          session: options.session,
        });
        if (result.data) {
          return result.data.verifyMFA;
        }
        return { success: false };
      } finally {
        setIsVerifying(false);
      }
    },
    []
  );

  const revokeDevice = React.useCallback(async (deviceKey: string) => {
    try {
      const result = await forgetTrustedDeviceMutation({ deviceKey });
      if (result.data?.forgetTrustedDevice) {
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  const disableMFA = React.useCallback(async () => {
    try {
      const result = await disableMFAMutation();
      if (result.data?.disableMFA) {
        setSetupComplete(false);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  const value: MFAContextState = React.useMemo(
    () => ({
      isSettingUp,
      qrCodeUrl,
      secretKey,
      setupComplete,
      isVerifying,
      initializeSetup,
      completeSetup,
      verifyCode,
      revokeDevice,
      disableMFA,
    }),
    [
      isSettingUp,
      qrCodeUrl,
      secretKey,
      setupComplete,
      isVerifying,
      initializeSetup,
      completeSetup,
      verifyCode,
      revokeDevice,
      disableMFA,
    ]
  );

  return <MFAContext.Provider value={value}>{children}</MFAContext.Provider>;
};

export const useMFA = (): MFAContextState => {
  const context = useContext(MFAContext);
  if (context === undefined) {
    throw new Error("useMFA must be used within a MFAProvider");
  }
  return context;
};
